import type { User } from '@supabase/supabase-js';
import type { AuthError, Session } from '@supabase/supabase-js';
import { createContext } from 'react';

// Define a type for JSON-compatible values for user metadata
type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

// Export the context type
export interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  signIn: (
    email: string,
    password: string,
    captchaToken?: string,
  ) => Promise<{ error: AuthError | null; data: Session | null }>;
  signUp: (
    email: string,
    password: string,
    options?: { data?: Record<string, JsonValue>; captchaToken?: string; emailRedirectTo?: string },
  ) => Promise<{
    error: AuthError | null;
    data: { user: User | null; session: Session | null };
  }>;
  signOut: () => Promise<void>;
  resetPasswordForEmail: (
    email: string,
    // captchaToken: Supabase Attack Protection rejects /auth/v1/recover with
    // `captcha_failed` before it even looks at the email, so the token is not
    // optional in practice — the signature simply could not carry one, which is
    // why password reset was impossible in production.
    options?: { redirectTo?: string; captchaToken?: string },
  ) => Promise<{ error: AuthError | null; data: { user: null } }>;
  resendSignupConfirmation: (
    email: string,
    options?: { captchaToken?: string; emailRedirectTo?: string },
  ) => Promise<{ error: AuthError | null }>;
  updatePassword: (
    password: string,
  ) => Promise<{ error: AuthError | null; data: { user: User | null } }>;
  signInWithGoogle: () => Promise<{ error: AuthError | null; data: null }>;
  signInWithApple: () => Promise<{ error: AuthError | null; data: null }>;
  /**
   * Establish a session from a one-time token minted server-side. A merchant
   * installing from the Shopify App Store has their account provisioned for
   * them by the OAuth callback, so they have no password to sign in with —
   * the callback hands the browser this token instead.
   */
  signInWithToken: (
    tokenHash: string,
  ) => Promise<{ error: AuthError | null; data: Session | null }>;
  refreshUser: () => Promise<void>;
}

// Export the default context value
export const defaultAuthContextValue: AuthContextType = {
  user: null,
  session: null,
  loading: true,
  error: null,
  signIn: async () => ({ error: null, data: null }),
  signUp: async () => ({ error: null, data: { user: null, session: null } }),
  signOut: async () => {},
  resetPasswordForEmail: async () => ({ error: null, data: { user: null } }),
  resendSignupConfirmation: async () => ({ error: null }),
  updatePassword: async () => ({ error: null, data: { user: null } }),
  signInWithGoogle: async () => ({ error: null, data: null }),
  signInWithApple: async () => ({ error: null, data: null }),
  signInWithToken: async () => ({ error: null, data: null }),
  refreshUser: async () => {},
};

// Export the context itself
export const AuthContext = createContext<AuthContextType | undefined>(undefined);
